import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { emitVisitCompleteWhenPhotosSettle, emitVisitError } from '../lib/embedBridge';
import ConciergeOtoGate from './intake/ConciergeOtoGate';
import DexaCreditsNote from './intake/DexaCreditsNote';
import { useHandoffSeed, skipHandoffContact } from '@/lib/handoff';
import { usePreview, PreviewInterstitial } from './preview/PreviewShell';
import QuestionStep from './intake/QuestionStep';
import MultiSelectStep from './intake/MultiSelectStep';
import InputStep from './intake/InputStep';
import HeightWeightBmiStep from './intake/HeightWeightBmiStep';
import TextareaStep from './intake/TextareaStep';
import DisqualifiedStep from './intake/DisqualifiedStep';
import BasicContactStep from './intake/BasicContactStep';
import ShippingAddressStep from './intake/ShippingAddressStep';
import ConsentDropdownStep from './intake/ConsentDropdownStep';
import FileUploadStep from './intake/FileUploadStep';
import PharmacySelectStep from './intake/PharmacySelectStep';
import AccountSetupCta from './intake/AccountSetupCta';
import Footer from './intake/Footer';
import CatalogCheckoutStep from './intake/CatalogCheckoutStep';
import BrandLogo from '@/components/BrandLogo';
import { buildTenantUrl } from '@/lib/tenant';
import {
  buildDynamicBrandedWeightlossFlow,
  isDisqualifyingAnswer,
  isNewPatient,
  avoidsFoundayo,
} from '../data/brandedWeightlossData';
import { resolveBrandedRoute, brandedProductFilter, anyBrandedProductFilter, lowestDosePick, ALL_PRODUCTS_PATH, BRANDED_PRODUCTS } from '../data/brandedProducts';
import { fastedDeclineDestination, fastedCompletionCopy, requiresFastedWindow } from '../data/fastedWindow';
import { apiService } from '../services/api';
import {
  consentAnswerLabel,
  composeQaQuestionText,
  describeSubmitFailure,
  formatMultiSelectAnswer,
  splitHeightWeightAnswer,
} from '../lib/utils';
import { MutatingDots } from 'react-loader-spinner';

const STORAGE_KEY = 'fitpal_bwl_progress';

// Commerce/delivery steps excluded from the Beluga Q/A section (Erika review
// 2026-06-17): pharmacy choice + search are not questionnaire items, and the
// Rx photo is submitted separately via the photo relay (no upload link / Q-A
// field needed). Their data still flows — pharmacy via the `pharmacySelection`
// envelope, the photo via uploadVisitPhoto — only the Q/A text is dropped.
const QA_EXCLUDE_SLIDES = new Set(['pharmacy-choice', 'pharmacy-search', 'rx-photo-upload']);

const ResumeModal = ({ onResume, onStartFresh }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Welcome back!</h3>
        <p className="text-gray-500">
          We found your previous progress. Would you like to continue where you left off?
        </p>
      </div>
      <div className="space-y-3">
        <button onClick={onResume} className="btn-primary w-full" data-testid="resume-progress-btn">
          Continue where I left off
        </button>
        <button
          onClick={onStartFresh}
          className="w-full py-3 px-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
          data-testid="start-fresh-btn"
        >
          Start fresh
        </button>
      </div>
    </div>
  </div>
);

const BrandedWeightlossForm = () => {
  // §branded-product-links: /branded/:slug narrows this same intake to one
  // product. /branded-weight-loss (no slug) is the all-products door, unchanged.
  //
  // An unknown or not-yet-live slug resolves to null and redirects rather than
  // rendering — every path returns HTTP 200 here, so without the redirect a
  // typo'd link is a blank page, not a 404.
  const { slug } = useParams();
  const route = resolveBrandedRoute(slug);
  // §6A can move the patient to a different product mid-flow (a fasted-window
  // decline routes to Foundayo), so the ACTIVE product is state, seeded from the
  // link. The URL deliberately does not change: the link they opened and the
  // product they end on are different facts, and §4 records both — entryProduct
  // stays the link, selectedProduct becomes what they bought. A wegovy-pill
  // entry with a foundayo-pill selection is a NORMAL outcome here, not a defect.
  const [switchedProduct, setSwitchedProduct] = useState(null);
  const brandedProduct = switchedProduct || route.product;

  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  // §authenticated-patient handoff: prefill contact from the resolved ?pt= patient.
  useHandoffSeed(setAnswers);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [slideDirection, setSlideDirection] = useState('initial');
  const [slideKey, setSlideKey] = useState(0);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualifyReason, setDisqualifyReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [visitResult, setVisitResult] = useState(null);

  // Synchronous re-entry guard for checkout submission (mirrors TrtForm).
  const submittingRef = useRef(false);

  const advancedForSlideKeyRef = useRef(null);
  const advanceTimerRef = useRef(null);
  useEffect(
    () => () => {
      if (advanceTimerRef.current !== null) clearTimeout(advanceTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.currentStep > 0) {
          setSavedProgress(parsed);
          setShowResumeModal(true);
        }
      }
    } catch (e) {
      console.error('Error loading saved progress:', e);
    }
  }, []);

  useEffect(() => {
    if (currentStep > 0 && !isComplete && !isDisqualified) {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ currentStep, answers, timestamp: Date.now() }),
        );
      } catch (e) {
        console.error('Error saving progress:', e);
      }
    }
  }, [currentStep, answers, isComplete, isDisqualified]);

  const clearSavedProgress = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing progress:', e);
    }
  }, []);

  // §resume-invariant: this restores the saved answers WHOLESALE, which is safe
  // only while every conditional in buildDynamicBrandedWeightlossFlow branches on
  // a key inside that same blob. If a slide ever becomes conditional on something
  // outside it (a per-product link, a prop, the URL), restoring here can carry one
  // product's answers onto another and silently skip a slide that should be
  // asked. See the §resume-invariant note on the builder in brandedWeightlossData.js.
  const handleResumeProgress = useCallback(() => {
    if (savedProgress) {
      setCurrentStep(savedProgress.currentStep);
      setAnswers(savedProgress.answers || {});
      setShowResumeModal(false);
    }
  }, [savedProgress]);

  const handleStartFresh = useCallback(() => {
    clearSavedProgress();
    setShowResumeModal(false);
    setSavedProgress(null);
  }, [clearSavedProgress]);

  // §resume-invariant: `brandedProduct` is passed IN, so a resumed answer set is
  // re-evaluated against the product the patient is on now — not the one that was
  // active when they saved. See the note on the builder.
  const preview = usePreview();
  const currentFlow = useMemo(
    () => preview.wrapFlow(skipHandoffContact(buildDynamicBrandedWeightlossFlow(answers, brandedProduct))),
    [answers, brandedProduct, preview],
  );
  const totalSteps = currentFlow.length;
  const currentQuestion = currentFlow[currentStep];

  const handleDisqualify = useCallback(
    (reason) => {
      setIsDisqualified(true);
      setDisqualifyReason(reason);
      clearSavedProgress();
      setSlideDirection('forward');
      setSlideKey((prev) => prev + 1);
    },
    [clearSavedProgress],
  );

  const handleAnswer = useCallback(
    (questionId, answer) => {
      const previousAnswer = answers[questionId];

      const mergedAnswers = { ...answers, [questionId]: answer };
      setAnswers(mergedAnswers);

      // §6A: a fasted-window decline ROUTES, it does not disqualify. Every other
      // consent decline in this flow ends at DisqualifiedStep; this one moves the
      // patient to Foundayo (orforglipron — no fasting requirement) and carries
      // on with the answers they have already given. Dead-ending someone who
      // simply cannot eat late is the failure this exists to prevent, and pushing
      // them back to an injection is a decision they already declined by choosing
      // a pill.
      if (questionId === 'fasted-window-ack') {
        const dest = fastedDeclineDestination(answer);
        if (dest && BRANDED_PRODUCTS[dest]) {
          setSwitchedProduct(BRANDED_PRODUCTS[dest]);
          // The acknowledgement and the morning-meds question are conditional on
          // the product, so they vanish from the flow on this switch. Drop their
          // answers too — leaving them behind would send Beluga a fasted
          // acknowledgement attached to a visit for a drug with no fasted window.
          setAnswers((prev) => {
            const next = { ...prev };
            delete next['fasted-window-ack'];
            delete next['morning-oral-meds'];
            return next;
          });
          return;
        }
      }

      // Third arg enables the cross-answer gates (BMI new>=25 / transfer>20,
      // oral-route-without-Foundayo).
      const disqualResult = isDisqualifyingAnswer(questionId, answer, mergedAnswers);
      if (disqualResult.disqualified) {
        handleDisqualify(disqualResult.reason);
        return;
      }

      if (currentQuestion?.type === 'single-select' && answer !== previousAnswer) {
        if (advancedForSlideKeyRef.current === slideKey) return;
        advancedForSlideKeyRef.current = slideKey;
        if (advanceTimerRef.current !== null) clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = setTimeout(() => {
          advanceTimerRef.current = null;
          setCurrentStep((prev) => {
            if (prev >= totalSteps - 1) return prev;
            setSlideDirection('forward');
            setSlideKey((k) => k + 1);
            return prev + 1;
          });
        }, 200);
      }
    },
    [totalSteps, currentQuestion, answers, handleDisqualify, slideKey],
  );

  const handleNext = useCallback(() => {
    if (currentQuestion) {
      const currentAnswer = answers[currentQuestion.id];
      const disqualResult = isDisqualifyingAnswer(currentQuestion.id, currentAnswer, answers);
      if (disqualResult.disqualified) {
        handleDisqualify(disqualResult.reason);
        return;
      }
    }
    if (currentStep < totalSteps - 1) {
      setSlideDirection('forward');
      setSlideKey((prev) => prev + 1);
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, totalSteps, currentQuestion, answers, handleDisqualify]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setSlideDirection('backward');
      setSlideKey((prev) => prev + 1);
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const handleStartOver = useCallback(() => {
    setSlideDirection('backward');
    setSlideKey((prev) => prev + 1);
    setCurrentStep(0);
    setAnswers({});
    setIsComplete(false);
    setIsDisqualified(false);
    setDisqualifyReason('');
    clearSavedProgress();
  }, [clearSavedProgress]);

  const getAnimationClass = () => {
    switch (slideDirection) {
      case 'forward':
        return 'slide-forward';
      case 'backward':
        return 'slide-backward';
      default:
        return 'slide-initial';
    }
  };

  // Bail before rendering anything: a bad slug must not start a form the patient
  // would then have to abandon.
  if (route.action === 'redirect') return <Navigate to={route.redirectTo} replace />;

  const renderStep = () => {
    if (isDisqualified) {
      return (
        <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
          <DisqualifiedStep reason={disqualifyReason} onStartOver={handleStartOver} />
        </div>
      );
    }

    if (!currentQuestion) return null;

    const commonProps = {
      question: preview.withIcons(currentQuestion),
      value: answers[currentQuestion.id],
      onAnswer: (answer) => handleAnswer(currentQuestion.id, answer),
      onNext: handleNext,
      onBack: handleBack,
      canGoBack: currentStep > 0,
      isLastStep: currentStep === totalSteps - 1,
      setLoading,
    };

    const stepContent = (() => {
      switch (currentQuestion.type) {
        case 'single-select':
          return <QuestionStep {...commonProps} />;
        case 'multi-select':
          return <MultiSelectStep {...commonProps} />;
        case 'input':
        case 'number':
        case 'date':
        case 'text':
          return <InputStep {...commonProps} />;
        case 'textarea':
          return <TextareaStep {...commonProps} />;
        case 'height-weight-bmi':
          return (
            <HeightWeightBmiStep {...commonProps} onDisqualify={handleDisqualify} isOnTreatment={false} />
          );
        case 'consent-dropdown':
          return <ConsentDropdownStep {...commonProps} onDisqualify={handleDisqualify} />;
        case 'file-upload':
          return <FileUploadStep {...commonProps} />;
        case 'pharmacy-select':
          return (
            <PharmacySelectStep
              {...commonProps}
              searchDefaults={{
                city: answers['shipping-address']?.city || '',
                state: answers['shipping-address']?.state || '',
                zip: answers['shipping-address']?.zipCode || '',
              }}
            />
          );
        case 'basic-contact':
          return <BasicContactStep {...commonProps} />;
        case 'shipping-address':
          return <ShippingAddressStep {...commonProps} />;
        case 'checkout':
          return (
            <CatalogCheckoutStep conciergeUpsell
              catalogVisitType="weightloss"
              // Partner-pharmacy shipping is not offered for branded meds
              // (owner 2026-10-18: price too high) — local or manufacturer only.
              allowShip={false}
              // Narrowed to the product the patient's link named, or every
              // branded SKU on the all-products door. NARROWED, NOT LOCKED —
              // "Modify your plan" still opens the list, because the flow's own
              // injection-ability question can contradict the link.
              filter={brandedProduct ? brandedProductFilter(brandedProduct) : anyBrandedProductFilter}
              // Default to the LOWEST DOSE, not the cheapest SKU. Five Mounjaro
              // strengths share one price, so the cheapest-SKU fallback tie-broke
              // on catalog order and defaulted a Mounjaro link to 10mg. Only
              // applied on a product link, where every SKU is one titration
              // ladder; the all-products door spans six drugs, where "lowest
              // dose" across brands is meaningless.
              recommendPick={brandedProduct ? lowestDosePick : undefined}
              onComplete={async (payment) => {
                if (submittingRef.current) return;
                submittingRef.current = true;
                try {
                  setIsSubmitting(true);
                  setAnswers((prev) => ({ ...prev, payment }));

                  const formattedAnswers = Object.entries(answers).flatMap(
                    ([questionId, answer]) => {
                      // §Beluga-review (Erika 2026-06-17): these are
                      // commerce/delivery steps, NOT clinical questionnaire
                      // items — exclude them from the Q/A section. The pharmacy
                      // pick still rides the `pharmacySelection` envelope, and
                      // the Rx photo still relays separately via
                      // uploadVisitPhoto — only their Q/A representation is
                      // dropped.
                      if (QA_EXCLUDE_SLIDES.has(questionId)) return [];
                      const split = splitHeightWeightAnswer(answer);
                      if (split) return split;
                      const slide = currentFlow.find((q) => q.id === questionId);
                      // '; ' so the backend can bullet multi-selects even when an
                      // option label contains ', ' (count-validated split, eb31b20).
                      const consentLabel = consentAnswerLabel(slide, answer, '; ');
                      let formattedAnswer;
                      if (consentLabel !== null) {
                        formattedAnswer = consentLabel;
                      } else if (Array.isArray(answer)) {
                        formattedAnswer = formatMultiSelectAnswer(answer);
                      } else if (typeof answer === 'object' && answer !== null) {
                        if (answer.preview) {
                          formattedAnswer = `File: ${answer.fileName || 'uploaded'}`;
                        } else {
                          formattedAnswer = JSON.stringify(answer);
                        }
                      } else {
                        formattedAnswer = String(answer);
                      }
                      const questionText = composeQaQuestionText(slide) || questionId;
                      const record = {
                        question: questionText,
                        answer: formattedAnswer,
                        slide_id: questionId,
                        rawAnswer: answer,
                      };
                      if (
                        slide &&
                        typeof slide.consentText === 'string' &&
                        slide.consentText.trim()
                      ) {
                        record.consentBody = slide.consentText;
                      }
                      return [record];
                    },
                  );

                  const contactInfo = answers['basic-contact'] || {};
                  const shipping = answers['shipping-address'] || {};
                  const paymentId =
                    payment?.paymentId ||
                    payment?.payment_id ||
                    localStorage.getItem('fitpal_intake_payment_id') ||
                    '';

                  // §named-field: sex-at-birth maps to the `sex` named field.
                  const sexRaw = answers['sex-at-birth'];
                  const sex = sexRaw ? sexRaw.charAt(0).toUpperCase() + sexRaw.slice(1) : '';

                  const selectedPlans = Array.isArray(payment?.selectedPlans)
                    ? payment.selectedPlans.filter(Boolean)
                    : [];

                  // PINK routing context for the backend's patientPreference
                  // builder (starter dose for new patients and for on-treatment
                  // patients without an Rx photo). See
                  // docs/BACKEND_BRANDED_WEIGHTLOSS_VISIT_PRD.md §4.
                  const newPatient = isNewPatient(answers);
                  const brandedRouting = {
                    newPatient,
                    starterDose:
                      newPatient || answers['rx-photo-available'] === 'no',
                    currentMedDose: answers['current-med-dose'] || null,
                    oralRoute: answers['injection-ability'] === 'no-need-oral',
                    avoidFoundayo: avoidsFoundayo(answers),
                  };

                  // Pharmacy choice — 'own' carries the Beluga PharmacyId the
                  // backend must use instead of the default branded pharmacy.
                  const ownPharmacy = answers['pharmacy-search'];
                  const pharmacySelection =
                    answers['pharmacy-choice'] === 'own' && ownPharmacy?.pharmacyId
                      ? {
                          mode: 'own',
                          pharmacyId: String(ownPharmacy.pharmacyId),
                          storeName: ownPharmacy.storeName || '',
                          city: ownPharmacy.city || '',
                          state: ownPharmacy.state || '',
                          zip: ownPharmacy.zip || '',
                        }
                      : { mode: 'default' };

                  const result = await apiService.submitForm(
                    formattedAnswers,
                    {
                      first_name: contactInfo.name?.split(' ')[0] || '',
                      last_name: contactInfo.name?.split(' ').slice(1).join(' ') || '',
                      email: contactInfo.email || '',
                      phone: contactInfo.phone || '',
                      dob: contactInfo.dob || '',
                      address: shipping.address1 || '',
                      address2: shipping.address2 || '',
                      city: shipping.city || '',
                      state: shipping.state || '',
                      zip: shipping.zipCode || '',
                      sex,
                    },
                    paymentId,
                    'brandedWeightloss',
                    'weightloss',
                    {
                      planId: selectedPlans[0] || null,
                      selectedPlans,
                      // §catalog: forward the exact catalog SKU selection so the
                      // BE builds patientPreference from these medIds (REPLACE,
                      // §5 precedence) and the charge already priced from the same
                      // set. Falls back to the legacy plan path when absent.
                      selectedMedIds:
                        (Array.isArray(payment?.selectedMedIds) && payment.selectedMedIds.length)
                          ? payment.selectedMedIds
                          : undefined,
                      brandedRouting,
                      pharmacySelection: payment?.pharmacySelection ?? pharmacySelection,
                      prescriptionFulfillment: payment?.prescriptionFulfillment,
                      // §dexa-add-on: forward what the patient actually bought.
                      // Without this the add-on is CHARGED (SquarePaymentForm gets
                      // amount=totalCents, which includes it) while the record of
                      // what it was for never leaves the browser.
                      dexaAddOn: payment?.dexaAddOn,
                      conciergeAddOn: payment?.conciergeAddOn,
                    },
                  );

                  // Relay uploaded photos (fire-and-forget): government ID +
                  // the on-treatment prescription photo.
                  ['identity-verification', 'rx-photo-upload'].forEach((slideId) => {
                    const photo = answers[slideId];
                    if (photo && photo.preview && result?.masterId) {
                      apiService
                        .uploadVisitPhoto({
                          masterId: result.masterId,
                          visitId: result.visitId,
                          slideId,
                          photo,
                        })
                        .catch((err) => {
                          console.warn(
                            `[uploadVisitPhoto] ${slideId} relay failed:`,
                            err?.response?.data || err.message,
                          );
                        });
                    }
                  });

                  setVisitResult(result);

                  // §embed-bridge: tell the dashboard what was created. Waits for any

                  // in-flight photo relay first — the parent closes its modal on this

                  // message, which unmounts the iframe and would abort the upload.

                  emitVisitCompleteWhenPhotosSettle({

                    masterId: result?.masterId,

                    visitId: result?.visitId,

                    scheduleLink: result?.scheduleLink,

                  });
                  setIsComplete(true);
                  handleNext();
                  clearSavedProgress();
                } catch (error) {
                  console.error('Branded weightloss visit creation error:', error);
                  // §embed-bridge: relay the RAW backend error — the dashboard reads
                  // errorCode/refundId to tell the patient their card was refunded.
                  emitVisitError(error);
                  alert(describeSubmitFailure(error));
                } finally {
                  submittingRef.current = false;
                  setIsSubmitting(false);
                }
              }}
              isProcessing={isSubmitting}
              answers={answers}
              setLoading={setLoading}
              clearSavedProgress={clearSavedProgress}
              primaryGoal={answers['primary-goal']}
              currentStep={currentStep}
              timestamp={Date.now()}
              email={answers['basic-contact']?.email}
            />
          );
        case 'complete':
          return (
            <ConciergeOtoGate answers={answers} visitType="brandedWeightloss" visitResult={visitResult}>
            <div className="max-w-xl mx-auto text-center py-10" data-testid="bwl-complete-step">
              <div className="w-16 h-16 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-5">
                <svg className="w-8 h-8 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Thanks{answers['basic-contact']?.name ? `, ${answers['basic-contact'].name.split(' ')[0]}` : ''}!
              </h2>
              <p className="text-gray-500 mb-2">
                Your intake has been submitted. A licensed provider will review your
                responses and follow up with next steps.
              </p>
              {/* §6A: repeat the fasted instruction on the confirmation screen —
                  but only when the product they ACTUALLY ended on carries it. A
                  patient who declined and switched to Foundayo must not see it,
                  or we have just told them to fast for no reason. */}
              {requiresFastedWindow(brandedProduct) && (
                <p className="text-sm text-gray-600 bg-[#f0faf4] border border-[#2d8a5f]/25 rounded-xl px-4 py-3 mb-4 text-left" data-testid="fasted-window-reminder">
                  {fastedCompletionCopy(true)}
                </p>
              )}
              {/* §preference-not-prescription: the product picked at checkout is a
                  request; the provider decides what is actually prescribed. */}
              <p className="text-sm text-gray-400 mb-4">
                Your provider will prescribe the treatment that&rsquo;s right for you,
                which may differ from the option you selected.
              </p>
              {visitResult?.masterId && (
                <div className="inline-flex flex-col items-center bg-gray-50 rounded-xl px-6 py-3">
                  <span className="text-xs uppercase tracking-wide text-gray-400">Visit reference</span>
                  <span className="text-lg font-semibold text-gray-900" data-testid="bwl-visit-ref">
                    {visitResult.masterId}
                  </span>
                </div>
              )}
              <AccountSetupCta email={answers['basic-contact']?.email} />
              <DexaCreditsNote answers={answers} />
            </div>
            </ConciergeOtoGate>
          );
        case 'preview-interstitial':
          return <PreviewInterstitial step={currentQuestion} onNext={handleNext} />;
        default:
          return <QuestionStep {...commonProps} />;
      }
    })();

    return (
      <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
        {preview.frame(currentQuestion, stepContent, handleBack)}
      </div>
    );
  };

  return (
    <>
      <div className="intake-form-loader" style={{ display: loading ? '' : 'none' }}>
        <MutatingDots />
      </div>

      <div
        className={`min-h-screen flex flex-col ${preview.rootClass}`}
        data-testid="bwl-form"
        style={{ display: loading === false ? '' : 'none' }}
      >
        {showResumeModal && (
          <ResumeModal onResume={handleResumeProgress} onStartFresh={handleStartFresh} />
        )}

        <header className="w-full py-4 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            {currentStep > 0 && !isComplete && !isDisqualified ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
                data-testid="back-button"
                style={{ display: currentQuestion?.type === 'complete' ? 'none' : '' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">Back</span>
              </button>
            ) : (
              <a href={buildTenantUrl('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0" data-testid="home-link">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">Home</span>
              </a>
            )}

            <div className="text-center flex-1">
              <BrandLogo
                alt="FitPal"
                className="mx-auto"
                style={{ width: '400px', height: 'auto', maxWidth: '100%' }}
              />
              <p className="text-xs text-gray-500 mt-1">Branded GLP-1 Weight Loss Program</p>
            </div>

            <div className="w-16 flex-shrink-0" />
          </div>
        </header>

        {!isComplete && !isDisqualified && totalSteps > 0 && (
          <div className="w-full px-4 py-2">
            <div className="max-w-2xl mx-auto">
              {preview.progress(currentQuestion, currentStep, totalSteps)}
            </div>
          </div>
        )}

        <main className="flex-1 flex flex-col items-center justify-start px-4 py-8">
          <div
            className={`w-full ${
              currentQuestion && currentQuestion.type === 'checkout' ? 'max-w-5xl' : 'max-w-xl'
            } slide-container`}
          >
            {renderStep()}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BrandedWeightlossForm;
